import { useState } from "react";
import { Skeleton } from "primereact/skeleton";
import { paymentsApi } from "../../services/payments/paymentsApi";
import Error from "../../components/common/Error";
import { DataTable } from "../../components/data/DataTable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { PaymentReportResponse } from "../../models/dtos";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import useFileDownload from "../../hooks/useFileDownload";
import { InputText } from "primereact/inputtext";

const PaymentFiles = () => {
  const { data: paymentFiles, error, isLoading } = paymentsApi.useGetPaymentFilesQuery();
  const [verifyPaymentFile] = paymentsApi.useVerifyPaymentFileMutation();
  const [updatePaymentAmount] = paymentsApi.useUpdatePaymentAmountMutation();

  const [triggerGetPaymentFile] = paymentsApi.useLazyGetPaymentFileQuery();
  const { downloadFile } = useFileDownload(triggerGetPaymentFile);

  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [amount, setAmount] = useState<number | null>(null);

  const handleDownload = (rowData: PaymentReportResponse) => {
    downloadFile({ paymentId: rowData.paymentId });
  };

  const handleVerify = (rowData: PaymentReportResponse, paymentApproved: boolean) => {
    verifyPaymentFile({
      paymentId: rowData.paymentId,
      paymentApproved,
    });
  };

  const handleAccept = (event: React.MouseEvent, item: PaymentReportResponse) => {
    confirmPopup({
      target: event.currentTarget as EventTarget & HTMLElement,
      message: "Сигурни ли сте, че искате да ОДОБРИТЕ това платежно нареждане?",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Да",
      rejectLabel: "Не",
      accept: () => handleVerify(item, true),
    });
  };

  const handleReject = (event: React.MouseEvent, item: PaymentReportResponse) => {
    confirmPopup({
      target: event.currentTarget as EventTarget & HTMLElement,
      message: "Сигурни ли сте, че искате да ОТХВЪРЛИТЕ това платежно нареждане?",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Да",
      rejectLabel: "Не",
      accept: () => handleVerify(item, false),
    });
  };

  const handleEdit = (rowData: PaymentReportResponse) => {
    setEditingRow(rowData.paymentId);
    setAmount(rowData.amount);
  };

  const handleSave = (rowData: PaymentReportResponse) => {
    updatePaymentAmount({
      paymentId: rowData.paymentId,
      amount: amount!,
    });

    setEditingRow(null);
    setAmount(null);
  };

  const actionBodyTemplate = (rowData: PaymentReportResponse) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-success"
          onClick={() => handleDownload(rowData)}
        />
        <Button
          icon="pi pi-check"
          className="p-button-rounded p-button-primary"
          onClick={(e) => handleAccept(e, rowData)}
        />
        <Button
          icon="pi pi-times"
          className="p-button-rounded p-button-danger"
          onClick={(e) => handleReject(e, rowData)}
        />
      </div>
    );
  };

  const amountBodyTemplate = (rowData: PaymentReportResponse) => {
    if (editingRow === rowData.paymentId) {
      return (
        <div className="flex gap-2 align-items-center">
          <InputText value={amount?.toString()} onChange={(e) => setAmount(parseInt(e.target.value))} />
          <Button icon="pi pi-save" className="p-button-rounded p-button-success" onClick={() => handleSave(rowData)} />
        </div>
      );
    } else {
      return (
        <div className="flex gap-2 align-items-center">
          {rowData.amount}
          <Button
            icon="pi pi-pencil"
            size="small"
            className="p-button-rounded p-button-secondary"
            onClick={() => handleEdit(rowData)}
          />
        </div>
      );
    }
  };

  if (isLoading) return <Skeleton className="w-full h-2rem" />;
  if (error) return <Error error={error} />;

  return (
    <div className="sticky top-0">
      <h1 className="mb-4 mt-0 text-4xl font-light">Платежни нареждания</h1>
      <DataTable value={paymentFiles?.data ?? []} emptyMessage="Не бяха открити платежни нареждания.">
        <Column field="facultyName" header="Име" sortable filter></Column>
        <Column field="paymentReference" header="Референция" sortable filter></Column>
        <Column field="studentFacultyNumber" header="Факултетен номер" sortable filter></Column>
        <Column field="studentFirstName" header="Име" sortable filter></Column>
        <Column field="studentLastName" header="Фамилия" sortable filter></Column>
        <Column field="specialityName" header="Специалност" sortable filter></Column>
        <Column field="specialityFeeName" header="Такса" sortable filter></Column>
        <Column field="amount" header="Сума" body={amountBodyTemplate} sortable filter></Column>
        <Column header="Действия" body={actionBodyTemplate}></Column>
      </DataTable>

      <ConfirmPopup />
    </div>
  );
};

export default PaymentFiles;
